import React, { createContext, useContext, useState } from "react";
import {
  DockPosition,
  DEFAULT_UI_SETTINGS,
  parseUISettings,
  formatUISettings,
} from "../utils/userPreferencesHelper";
import userPreferenceApiService from "../services/user-preference-api";

interface DockContextType {
  dockPosition: DockPosition;
  setDockPosition: (position: DockPosition) => void;
  initializeFromPreferences: (settingsField: string | undefined | null) => void;
}

const DockContext = createContext<DockContextType | undefined>(undefined);

const DOCK_POSITION_KEY = "dock-position";

export const DockProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dockPosition, setDockPositionState] = useState<DockPosition>(() => {
    // Load dock position from localStorage
    const savedPosition = localStorage.getItem(DOCK_POSITION_KEY);
    return (savedPosition as DockPosition) || DEFAULT_UI_SETTINGS.dockPosition;
  });

  const setDockPosition = async (position: DockPosition) => {
    setDockPositionState(position);
    localStorage.setItem(DOCK_POSITION_KEY, position);

    // Save to backend
    try {
      // Get current preferences
      const currentPrefs = await userPreferenceApiService.getMyPreferences();

      // Parse current settings field to get display mode
      const uiSettings = parseUISettings(currentPrefs.settings);

      // Update dock position
      uiSettings.dockPosition = position;

      // Format and save
      const updatedSettingsField = formatUISettings(uiSettings);
      await userPreferenceApiService.saveMyPreferences({
        ...currentPrefs,
        settings: updatedSettingsField,
      });
    } catch (error) {
      console.error("Failed to save dock position to backend:", error);
      // Continue with localStorage-only fallback
    }
  };

  const initializeFromPreferences = (
    settingsField: string | undefined | null,
  ) => {
    const uiSettings = parseUISettings(settingsField);
    setDockPositionState(uiSettings.dockPosition);
    localStorage.setItem(DOCK_POSITION_KEY, uiSettings.dockPosition);
  };

  return (
    <DockContext.Provider
      value={{ dockPosition, setDockPosition, initializeFromPreferences }}
    >
      {children}
    </DockContext.Provider>
  );
};

export const useDock = (): DockContextType => {
  const context = useContext(DockContext);
  if (!context) {
    throw new Error("useDock must be used within a DockProvider");
  }
  return context;
};
